import React, { useEffect } from 'react';
import { useParams, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { CheckCircle, XCircle, Clock, Loader2, ArrowRight, Home, LayoutDashboard, LifeBuoy } from 'lucide-react';
import { useAuth } from '../AuthContext';
import { getContractSuccessSteps, type ContractType } from '../../utils/contractSteps';
import { trackPurchase } from '../../utils/facebookPixel';

const PaymentStatusPage: React.FC = () => {
  const { status } = useParams<{ status: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Obtém a função para refrescar o usuário e o estado de carregamento do AuthContext
  const { refreshAuthUser, isLoading, isAuthenticated } = useAuth();

  const appointmentId = searchParams.get('appointment');
  const stateMessage = location.state?.message as string | undefined;
  const identityAssurance = location.state?.identityAssurance as 'authenticated' | 'email-only' | undefined;
  const identityOutcome = location.state?.identityOutcome as 'existing-user' | 'new-user-password-set' | 'new-user-password-pending' | undefined;
  const checkoutEmail = location.state?.checkoutEmail as string | undefined;
  const contractType = location.state?.contractType as ContractType | undefined;
  const purchaseAmount = location.state?.amount as number | undefined;
  const purchaseCurrency = (location.state?.currency as string | undefined) || 'BRL';
  const planName = location.state?.planName as string | undefined;
  const purchaseChargeId = location.state?.chargeId as string | undefined;
  const purchaseSubscriptionId = location.state?.subscriptionId as string | undefined;
  // ID de deduplicação: precisa casar com o evento server-side (Conversions API).
  const purchaseEventId = purchaseSubscriptionId
    ? `purchase_sub_${purchaseSubscriptionId}`
    : purchaseChargeId
      ? `purchase_${purchaseChargeId}`
      : undefined;
  const supportUrl = 'https://wa.me/5513997701755?text=Ol%C3%A1!%20Preciso%20de%20ajuda%20com%20meu%20pagamento.';

  // Efeito que é executado assim que a página carrega.
  // Ele força a re-verificação da sessão do usuário, resolvendo o problema de "logout".
  useEffect(() => {
    refreshAuthUser();
  }, [refreshAuthUser]);

  useEffect(() => {
    if (status !== 'sucesso') return;

    trackPurchase({
      value: purchaseAmount,
      currency: purchaseCurrency,
      contentName: planName,
      eventId: purchaseEventId,
    });
  }, [status, purchaseAmount, purchaseCurrency, planName, purchaseEventId]);

  let statusConfig = {
    icon: <Clock className="w-16 h-16 text-gray-400" />,
    title: 'Pagamento Pendente',
    message: 'O seu agendamento está pendente de confirmação do pagamento. Iremos notificá-lo assim que for aprovado.',
    bgColor: 'bg-gray-100',
    textColor: 'text-gray-800',
  };

  if (status === 'sucesso') {
    statusConfig = {
      icon: <CheckCircle className="w-16 h-16 text-green-500" />,
      title: 'Pagamento Aprovado!',
      message: stateMessage || 'Seu pagamento foi processado com sucesso. Agora você já pode seguir para os próximos passos.',
      bgColor: 'bg-green-50',
      textColor: 'text-green-800',
    };
  } else if (status === 'falha') {
    statusConfig = {
      icon: <XCircle className="w-16 h-16 text-red-500" />,
      title: 'Falha no Pagamento',
      message: 'Houve um problema ao processar o seu pagamento. Por favor, tente novamente ou contate o suporte.',
      bgColor: 'bg-red-50',
      textColor: 'text-red-800',
    };
  }

  const primaryActionIsAuthenticated = isAuthenticated;
  const isEmailOnlySuccess = status === 'sucesso' && identityAssurance === 'email-only';

  const handlePrimaryAction = () => {
    if (isEmailOnlySuccess) {
      if (identityOutcome === 'new-user-password-pending') {
        navigate('/esqueci-senha', { state: { email: checkoutEmail } });
        return;
      }

      navigate('/login', { state: { email: checkoutEmail, from: { pathname: '/painel' } } });
      return;
    }

    const redirectAfterPlanPurchase = sessionStorage.getItem("redirectAfterPlanPurchase");
    if (redirectAfterPlanPurchase) {
      sessionStorage.removeItem("redirectAfterPlanPurchase");
      window.location.pathname = redirectAfterPlanPurchase;
      return;
    }

    if (!primaryActionIsAuthenticated) {
      navigate('/');
      return;
    }

    navigate('/painel');
  };

  const getPrimaryButtonLabel = () => {
    if (isEmailOnlySuccess) {
      if (identityOutcome === 'new-user-password-pending') {
        return 'Definir senha';
      }

      return 'Entrar na conta';
    }

    if (isLoading && primaryActionIsAuthenticated) {
      return <Loader2 className="w-5 h-5 animate-spin" />;
    }

    const redirectAfterPlanPurchase = sessionStorage.getItem("redirectAfterPlanPurchase");
    if (redirectAfterPlanPurchase) {
      return 'Sair';
    }

    return primaryActionIsAuthenticated ? 'Ir para o meu Painel' : 'Ir para a Home';
  };

  const nextSteps = status === 'sucesso'
    ? isEmailOnlySuccess
      ? [
          ...getContractSuccessSteps(contractType),
          `Para acessar os seus benefícios, entre na conta usando o e-mail informado${checkoutEmail ? `: ${checkoutEmail}` : '.'}`,
        ]
      : getContractSuccessSteps(contractType)
    : status === 'falha'
      ? [
          'Revise os dados informados e tente novamente.',
          'Se o erro persistir, escolha outro método de pagamento.',
          'Caso continue com dúvida, acione o suporte para validar o pedido.',
        ]
      : [
          'Aguarde a confirmação automática do pagamento.',
          'Se você pagou por PIX, a compensação costuma aparecer em instantes.',
          'Se houver demora, volte ao painel ou acione o suporte para checagem.',
        ];

  return (
    <div className={`min-h-screen flex items-center justify-center p-4 ${statusConfig.bgColor}`}>
      <div className="bg-white p-8 sm:p-12 rounded-2xl shadow-lg text-center max-w-3xl w-full">
        <div className="mx-auto mb-6 w-20 h-20 flex items-center justify-center">
            {statusConfig.icon}
        </div>
        <h1 className={`text-3xl font-bold ${statusConfig.textColor} mb-4`}>
          {statusConfig.title}
        </h1>
        <p className="text-gray-600 mb-8 text-lg">
          {statusConfig.message}
        </p>
        {appointmentId && (
            <p className="text-sm text-gray-500 mb-8">
                ID do Agendamento: {appointmentId}
            </p>
        )}

        <div className="rounded-2xl border border-gray-200 bg-gray-50 p-6 text-left mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Próximos passos</h2>
          <div className="space-y-3">
            {nextSteps.map((step, index) => (
              <div key={step} className="flex items-start gap-3 text-sm text-gray-700">
                <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-white text-xs font-semibold text-gray-700 shadow-sm">
                  {index + 1}
                </span>
                <span>{step}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row justify-center gap-4">
            {/* O botão agora usa a função de navegação e mostra um estado de carregamento */}
            <button 
              onClick={handlePrimaryAction}
              disabled={isLoading && primaryActionIsAuthenticated && !isEmailOnlySuccess}
              className="px-8 py-3 bg-gray-700 text-white font-semibold rounded-full hover:bg-gray-800 transition disabled:bg-gray-400 flex items-center justify-center gap-2"
            >
              {(primaryActionIsAuthenticated && !isEmailOnlySuccess) ? <LayoutDashboard className="w-5 h-5" /> : <Home className="w-5 h-5" />}
              {getPrimaryButtonLabel()}
            </button>
            <a
              href={supportUrl}
              target="_blank"
              rel="noreferrer"
              className="px-8 py-3 bg-teal-600 text-white font-semibold rounded-full hover:bg-teal-700 transition flex items-center justify-center gap-2"
            >
              <LifeBuoy className="w-5 h-5" />
              Falar com o suporte
            </a>
            {status === 'falha' && (
                 <button onClick={() => navigate('/agendamento')} className="px-8 py-3 bg-teal-600 text-white font-semibold rounded-full hover:bg-teal-700 transition flex items-center justify-center gap-2">
                    <ArrowRight className="w-5 h-5" />
                    Tentar Novamente
                </button>
            )}
        </div>
      </div>
    </div>
  );
};

export default PaymentStatusPage;

